import React from "react";
// ============================================================================
// TemplateSelector Component
// ============================================================================
// Purpose: Modal for selecting and applying templates to ads in bulk
// Matches AdNova's "Choose Ad Copy Template" modal design
// ============================================================================

import { useState, useEffect } from 'react';
import { useTemplates } from '@/hooks/useTemplates';
import { AdTemplate, TemplateFieldSelection, DEFAULT_FIELD_SELECTION, TEMPLATE_FIELD_LABELS } from '@/types/template';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Label } from '@/components/ui/label';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Card } from '@/components/ui/card';
import { Separator } from '@/components/ui/separator';

interface TemplateSelectorProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  selectedRowCount: number; // Number of currently selected rows
  totalRowCount: number; // Total number of rows
  onApply: (templateId: string, fieldSelection: TemplateFieldSelection, applyTo: 'selected' | 'all') => void;
  adAccountId?: string; // Optional filter by ad account
}

export function TemplateSelector({
  open,
  onOpenChange,
  selectedRowCount,
  totalRowCount,
  onApply,
  adAccountId,
}: TemplateSelectorProps) {
  const { templates, fetchTemplates, fetchTemplate, loading } = useTemplates();
  const [selectedTemplateId, setSelectedTemplateId] = useState<string>('');
  const [selectedTemplate, setSelectedTemplate] = useState<AdTemplate | null>(null);
  const [fieldSelection, setFieldSelection] = useState<TemplateFieldSelection>(DEFAULT_FIELD_SELECTION);
  const [applyTo, setApplyTo] = useState<'selected' | 'all'>(selectedRowCount > 0 ? 'selected' : 'all');

  // Fetch templates when modal opens
  useEffect(() => {
    if (open) {
      fetchTemplates(adAccountId);
    }
  }, [open, adAccountId]);

  // Load full template details when selected
  useEffect(() => {
    if (selectedTemplateId) {
      loadTemplateDetails(selectedTemplateId);
    } else {
      setSelectedTemplate(null);
    }
  }, [selectedTemplateId]);

  const loadTemplateDetails = async (templateId: string) => {
    const template = await fetchTemplate(templateId);
    setSelectedTemplate(template);
  };

  const handleFieldToggle = (field: keyof TemplateFieldSelection) => {
    setFieldSelection((prev) => ({
      ...prev,
      [field]: !prev[field],
    }));
  };

  const handleSelectAll = () => {
    const allSelected = Object.values(fieldSelection).every((val) => val);
    const newSelection = Object.keys(fieldSelection).reduce(
      (acc, key) => ({ ...acc, [key]: !allSelected }),
      {} as TemplateFieldSelection
    );
    setFieldSelection(newSelection);
  };

  const handleApply = () => {
    if (selectedTemplateId) {
      onApply(selectedTemplateId, fieldSelection, applyTo);
      onOpenChange(false);
      // Reset state
      setSelectedTemplateId('');
      setFieldSelection(DEFAULT_FIELD_SELECTION);
    }
  };

  const handleCancel = () => {
    onOpenChange(false);
    // Reset state
    setSelectedTemplateId('');
    setFieldSelection(DEFAULT_FIELD_SELECTION);
  };

  const selectedFieldCount = Object.values(fieldSelection).filter(Boolean).length;
  const canApply = selectedTemplateId && selectedFieldCount > 0;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Choose Ad Copy Template</DialogTitle>
          <DialogDescription>
            Select a template and choose which fields to import into your ads
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-6 py-4">
          {/* Template Selector */}
          <div className="space-y-2">
            <Label htmlFor="template-select">Select Template</Label>
            <Select
              value={selectedTemplateId}
              onValueChange={setSelectedTemplateId}
            >
              <SelectTrigger id="template-select">
                <SelectValue placeholder="Choose a template..." />
              </SelectTrigger>
              <SelectContent>
                {templates.length === 0 ? (
                  <div className="py-6 text-center text-sm text-muted-foreground">
                    No templates found. Create one in Templates page.
                  </div>
                ) : (
                  templates.map((template) => (
                    <SelectItem key={template.id} value={template.id}>
                      {template.name}
                    </SelectItem>
                  ))
                )}
              </SelectContent>
            </Select>
          </div>

          {/* Field Selection */}
          {selectedTemplateId && (
            <Card className="p-4">
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <Label className="text-base font-semibold">
                    Select Fields to Import
                  </Label>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={handleSelectAll}
                  >
                    {Object.values(fieldSelection).every((val) => val) ? 'Deselect All' : 'Select All'}
                  </Button>
                </div>

                <Separator />

                <div className="grid grid-cols-2 gap-4">
                  {(Object.keys(fieldSelection) as Array<keyof TemplateFieldSelection>).map((field) => (
                    <div key={field} className="flex items-center space-x-2">
                      <Checkbox
                        id={field}
                        checked={fieldSelection[field]}
                        onCheckedChange={() => handleFieldToggle(field)}
                      />
                      <Label
                        htmlFor={field}
                        className="text-sm font-normal cursor-pointer"
                      >
                        {TEMPLATE_FIELD_LABELS[field]}
                      </Label>
                    </div>
                  ))}
                </div>

                <div className="pt-2 text-sm text-muted-foreground">
                  {selectedFieldCount} field{selectedFieldCount !== 1 ? 's' : ''} selected
                </div>
              </div>
            </Card>
          )}

          {/* Apply To Selection */}
          {selectedTemplateId && (
            <div className="space-y-3">
              <Label className="text-base font-semibold">Import Options</Label>
              <RadioGroup
                value={applyTo}
                onValueChange={(value: 'selected' | 'all') => setApplyTo(value)}
              >
                <div className="flex items-center space-x-2">
                  <RadioGroupItem value="selected" id="selected" disabled={selectedRowCount === 0} />
                  <Label htmlFor="selected" className="font-normal cursor-pointer">
                    Update only selected rows ({selectedRowCount})
                  </Label>
                </div>
                <div className="flex items-center space-x-2">
                  <RadioGroupItem value="all" id="all" />
                  <Label htmlFor="all" className="font-normal cursor-pointer">
                    Update all Rows ({totalRowCount})
                  </Label>
                </div>
              </RadioGroup>
            </div>
          )}

          {/* Preview (if template loaded) */}
          {selectedTemplate && (
            <Card className="p-4 bg-muted/50">
              <div className="space-y-2">
                <Label className="text-sm font-semibold">Template Preview</Label>
                <div className="space-y-1 text-sm">
                  {selectedTemplate.description && (
                    <p className="text-muted-foreground">{selectedTemplate.description}</p>
                  )}
                  <div className="flex flex-wrap gap-2 pt-2">
                    {selectedTemplate.primary_text_1 && (
                      <span className="inline-flex items-center rounded-md bg-blue-50 px-2 py-1 text-xs font-medium text-blue-700">
                        Primary Texts: {[1, 2, 3, 4, 5].filter(i => selectedTemplate[`primary_text_${i}` as keyof AdTemplate]).length}
                      </span>
                    )}
                    {selectedTemplate.headline_1 && (
                      <span className="inline-flex items-center rounded-md bg-green-50 px-2 py-1 text-xs font-medium text-green-700">
                        Headlines: {[1, 2, 3, 4, 5].filter(i => selectedTemplate[`headline_${i}` as keyof AdTemplate]).length}
                      </span>
                    )}
                    {selectedTemplate.cta_button && (
                      <span className="inline-flex items-center rounded-md bg-purple-50 px-2 py-1 text-xs font-medium text-purple-700">
                        CTA: {selectedTemplate.cta_button}
                      </span>
                    )}
                  </div>
                  {/* Enhancement indicator */}
                  <div className="mt-3 pt-3 border-t border-border">
                    <p className="text-xs text-muted-foreground">
                      Creative Enhancements from this template will be applied automatically when ads are created.
                    </p>
                  </div>
                </div>
              </div>
            </Card>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={handleCancel}>
            Back
          </Button>
          <Button onClick={handleApply} disabled={!canApply || loading}>
            Apply
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
